import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "@/components/ui/Spinner";
import type { Toast, ToastVariant } from "@/components/ui/toast/toastStore";

const VARIANT_STYLES: Record<ToastVariant, { accent: string; icon: string; ring: string }> = {
  success: {
    accent: "bg-status-green",
    icon: "text-status-green",
    ring: "ring-status-green/30",
  },
  error: {
    accent: "bg-status-red",
    icon: "text-status-red",
    ring: "ring-status-red/30",
  },
  warning: {
    accent: "bg-status-orange",
    icon: "text-status-orange",
    ring: "ring-status-orange/30",
  },
  info: {
    accent: "bg-accent",
    icon: "text-accent",
    ring: "ring-accent/30",
  },
  loading: {
    accent: "bg-accent",
    icon: "text-accent",
    ring: "ring-accent/30",
  },
};

function ToastIcon({ variant }: { variant: ToastVariant }) {
  const cls = clsx("h-5 w-5 shrink-0", VARIANT_STYLES[variant].icon);
  switch (variant) {
    case "success":
      return <CheckCircleIcon className={cls} aria-hidden />;
    case "error":
      return <XCircleIcon className={cls} aria-hidden />;
    case "warning":
      return <ExclamationTriangleIcon className={cls} aria-hidden />;
    case "loading":
      return <Spinner className={clsx("h-5 w-5", VARIANT_STYLES[variant].icon)} />;
    default:
      return <InformationCircleIcon className={cls} aria-hidden />;
  }
}

export function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef<number | null>(null);
  const styles = VARIANT_STYLES[toast.variant];

  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => onDismiss(toast.id), 180);
  }, [onDismiss, toast.id]);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (toast.duration > 0) {
      timerRef.current = window.setTimeout(close, toast.duration);
    }
  }, [clearTimer, close, toast.duration]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  const handleAction = useCallback(async () => {
    if (!toast.action) return;
    const keepOpen = await toast.action.onClick();
    if (keepOpen !== false) close();
  }, [toast.action, close]);

  return (
    <div
      role={toast.variant === "error" || toast.variant === "warning" ? "alert" : "status"}
      aria-live={toast.variant === "error" || toast.variant === "warning" ? "assertive" : "polite"}
      className={clsx(
        "eh-toast pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-ios border border-separator bg-card-elevated px-3.5 py-3 pr-9 shadow-ios-md ring-1",
        styles.ring,
        leaving ? "eh-toast--leaving" : "eh-toast--entering",
      )}
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
    >
      <span className={clsx("absolute inset-y-0 left-0 w-1", styles.accent)} aria-hidden />
      <ToastIcon variant={toast.variant} />
      <div className="min-w-0 flex-1">
        {toast.title ? (
          <p className="text-sm font-semibold text-label">{toast.title}</p>
        ) : null}
        {toast.message != null ? (
          <div className={clsx("text-sm text-label", toast.title && "mt-0.5 text-label-secondary")}>
            {toast.message}
          </div>
        ) : null}
        {toast.action ? (
          <button
            type="button"
            className="mt-2 text-sm font-semibold text-accent hover:underline"
            onClick={() => void handleAction()}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      {toast.dismissible ? (
        <button
          type="button"
          aria-label="Schließen"
          className="absolute right-1.5 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-label-tertiary transition hover:bg-fill-tertiary hover:text-label"
          onClick={close}
        >
          <XMarkIcon className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
